import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';
import { cx } from './cx';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Échap tapé dans une autre fenêtre modale que `node` (ouverte par-dessus : confirmation de sécurité, autre
 * confirmation) : c'est à elle de l'annuler, pas à `node` de se fermer à sa place.
 */
const fromOtherModal = (event: KeyboardEvent, node: HTMLElement | null) =>
  !!node && event.target instanceof Element && !node.contains(event.target) && !!event.target.closest('[aria-modal="true"]');

/** Garde le focus clavier dans `el` (Tab / Maj+Tab) et le rend à l'élément d'origine à la fermeture. */
export function useFocusTrap(open: boolean, el: RefObject<HTMLElement>, onEscape?: () => void) {
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const node = el.current;
    const first = node?.querySelector<HTMLElement>('[data-autofocus]') ?? node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onEscape) {
        if (fromOtherModal(event, node)) return;
        event.stopPropagation();
        onEscape();
        return;
      }
      if (event.key !== 'Tab' || !node) return;
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((i) => i.offsetParent !== null);
      if (!items.length) return;
      const firstItem = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      previous?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

interface DrawerProps {
  open: boolean;
  /** Demande de fermeture (Échap, clic à côté, croix). Le parent décide (ex. saisie en cours). */
  onClose: () => void;
  title: ReactNode;
  /** Ligne sous le titre (date, statut…). */
  subtitle?: ReactNode;
  /** Élément à gauche du titre (avatar, icône). */
  leading?: ReactNode;
  children: ReactNode;
  /** Barre d'actions fixée en bas. */
  footer?: ReactNode;
  /** Largeur sur ordinateur (plein écran sur téléphone). */
  width?: number;
  /** Pictogrammes d'action affichés à gauche de la croix (copier, partager…). */
  actions?: ReactNode;
  className?: string;
}

/**
 * Panneau latéral (charte) : glisse depuis la droite, en-tête et pied fixes, contenu qui défile.
 * Échap, clic à côté et croix appellent `onClose` ; focus clavier gardé dans le panneau.
 */
export function Drawer({ open, onClose, title, subtitle, leading, children, footer, width = 440, actions, className }: DrawerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(open, ref, onClose);
  if (!open) return null;
  return createPortal(
    <div className="charte-root v2-root fixed inset-0 z-[80] font-sans tracking-[-.2px]">
      <div className="absolute inset-0 bg-[#0F172A]/25 backdrop-blur-[1px] animate-v2-fade-in" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx(
          'drawer-panel absolute inset-y-0 right-0 flex w-full flex-col bg-white shadow-[-24px_0_60px_-24px_rgba(15,23,42,0.25)] outline-none sm:inset-y-3 sm:right-3 sm:rounded-[24px]',
          className,
        )}
        style={{ maxWidth: width }}
      >
        <header className="flex shrink-0 items-start gap-3 border-b border-slate-100 px-6 pb-4 pt-5">
          {leading}
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="truncate text-[17px] font-semibold tracking-[-.4px] text-heading">
              {title}
            </h2>
            {subtitle && <div className="mt-0.5 text-[13px] text-secondary">{subtitle}</div>}
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {actions}
            <Button variant="ghost" size="sm" iconOnly icon={X} aria-label="Fermer" onClick={onClose} />
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <footer className="flex shrink-0 flex-wrap items-center gap-2 border-t border-slate-100 px-6 py-4">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
